/**
 * Services that are command-line tools, not servers (vfs-service `src/preview/cliTool.js`): a Go program with cobra
 * that prints its help and exits. Flow used to start one like a web app: it exited, was started again, and the header
 * stayed on "Starting 5/6", Apps on Building, the Preview on its spinner. Now:
 *
 * - vfs-service's `GET /projects/:id/services` names them (`cli: { "services/<name>": { tool, stack, configured,
 *   detected } }`, and `cli: { tool, configured }` on their runner entries); SandboxService remembers the map per
 *   project (`rememberCliServices`) so any code with a project id can ask (`cliServiceOf`), as utils/repoFlags does.
 * - A CLI is "ready" once it builds: its state is `built` (never `running`, `starting` or `crashed`), it is not
 *   counted among the running services, Start all does not wait for it (one set up as one is built in passing), and
 *   one not set up as one ("Command-line tool" in Apps › How it runs) is never started.
 * - Its Preview is Try it (components/preview/CliTryIt.jsx): a command, Run, and the output.
 *
 * Set up as one, its `.flow/service.json` is a worker with an idle start (CLI_START): the runner builds it and keeps
 * its preview dir, Flow reads the pair as "Command-line tool" (utils/builderModel.js).
 */

/** The idle start of a command-line tool (vfs-service cliTool.js CLI_START). */
export const CLI_START = 'exec tail -f /dev/null';

const commandOf = (value) => (Array.isArray(value) ? value.join(' ') : String(value || '')).trim();

/** Whether a service.json is Flow's "Command-line tool": a worker whose start is CLI_START. */
export const isCliSpec = (spec) => Boolean(spec) && spec.kind === 'worker' && commandOf(spec.start) === CLI_START;

const known = new Map(); // projectId -> { [serviceName]: info }
const short = (name) => String(name || '').trim().replace(/^\.?\/+/, '').replace(/\/+$/, '');
const canonical = (name) => (short(name).includes('/') ? short(name) : `services/${short(name)}`);

/** Remembers the CLIs of a project's services answer (`cli`); an answer without it (an older vfs-service) clears them. */
export function rememberCliServices(projectId, map) {
  if (!projectId) { return; }
  known.set(projectId, map && typeof map === 'object' ? map : {});
}

/** The CLI `serviceName` of `projectId` is (`{ tool, stack, configured, detected }`), or null. */
export function cliServiceOf(projectId, serviceName) {
  if (!projectId || !serviceName) { return null; }
  return known.get(projectId)?.[canonical(serviceName)] || null;
}

/** Tests only: forget every project's CLIs. */
export function resetCliServices() {
  known.clear();
}

/** Whether a run item (`{ serviceName, service }`) of `projectId` is a CLI: its runner entry says so, or the answer did. */
export const isCliItem = (projectId, { serviceName, service } = {}) => Boolean(service?.cli) || Boolean(cliServiceOf(projectId, serviceName));

/**
 * The state of a CLI's runner entry: building | failed | built (its build is done: its idle process runs, or ran and
 * was stopped, the build stays for Try it) | stopped (never built, or stopped while it built).
 */
export function cliState(service) {
  if (!service) { return 'stopped'; }
  if (service.buildStatus === 'building') { return 'building'; }
  if (service.buildStatus === 'failed' || service.buildStatus === 'error') { return 'failed'; }
  if (service.buildPhase === 'cancelled') { return 'stopped'; }
  return service.containerStatus === 'running' || ['ready', 'completed'].includes(service.buildStatus) ? 'built' : 'stopped';
}
